import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone } from "lucide-react";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { broadcastPreview, broadcastSend, broadcastGeo } from "../../api/platform";
import { SIZE_BUCKETS } from "./notifyBuckets";
import { NotifyComposer } from "./NotifyComposer";
import { NotifyFilters } from "./NotifyFilters";
import { NotifyWorldMap } from "./NotifyWorldMap";
import { NotifyHistory } from "./NotifyHistory";

const EMPTY = { title_ar: "", title_en: "", body_ar: "", body_en: "", country: "", region: "", city: "", bucket: "", companyId: "", adminsOnly: false };

export const PlatformNotify = ({ companies = [] }) => {
  const [form, setForm] = useState(EMPTY);
  const [ch, setCh] = useState({ inapp: true, push: true, email: false });
  const [geo, setGeo] = useState({});
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(0);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const loadGeo = () => broadcastGeo().then(setGeo).catch(() => {});

  useEffect(() => { loadGeo(); }, []);

  const filter = () => {
    const f = { admins_only: form.adminsOnly };
    if (form.companyId) { f.company_id = form.companyId; return f; }
    if (form.country) f.country = form.country;
    if (form.region) f.region = form.region;
    if (form.city) f.city = form.city;
    if (form.bucket) { const b = SIZE_BUCKETS.find((x) => x.id === form.bucket); f.size_min = b.min; f.size_max = b.max; }
    return f;
  };

  const preview = async () => { try { setCount(await broadcastPreview(filter())); } catch { toast.error("تعذّر حساب العدد"); } };
  const send = async () => {
    if (!(form.title_ar.trim() || form.title_en.trim()) || !(form.body_ar.trim() || form.body_en.trim())) return toast.error("أدخل العنوان والنص");
    if (!ch.inapp && !ch.push && !ch.email) return toast.error("اختر قناة إرسال واحدة على الأقل");
    setBusy(true);
    try {
      const r = await broadcastSend({ filter: filter(), title_ar: form.title_ar, title_en: form.title_en, body_ar: form.body_ar, body_en: form.body_en, channels: ch });
      let msg = `تم الإرسال إلى ${r.recipients} مستخدم في ${r.companies} شركة`;
      if (r.push) {
        const p = r.push;
        msg += ` · إشعار الجوال وصل إلى ${p.sent} جهاز`;
        if (p.no_device) msg += ` (${p.no_device} مستخدم بلا جهاز مُفعّل للدفع)`;
        if (!p.sent && p.error) msg += ` — تعذّر الدفع: ${p.error}`;
      }
      if (r.push && !r.push.sent) toast.warning(msg); else toast.success(msg);
      setForm((f) => ({ ...f, title_ar: "", title_en: "", body_ar: "", body_en: "" })); setCount(null); loadGeo(); setSent((n) => n + 1);
    } catch { toast.error("فشل الإرسال"); } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-4">
      <NotifyWorldMap geo={geo} />
      <NeuCard data-testid="platform-notify">
        <p className="label-xs flex items-center gap-2"><Megaphone className="h-4 w-4 text-orange-500" /> إشعار ترويجي مُستهدَف</p>
        <div className="mt-4 grid gap-4">
          <NotifyComposer form={form} set={set} />
          <NotifyFilters form={form} set={setForm} ch={ch} setCh={setCh} companies={companies} />
          {count ? <p className="text-sm font-bold text-primary" data-testid="notify-count">سيصل إلى {count.recipients} مستخدم في {count.companies} شركة</p> : null}
          <div className="flex gap-3">
            <NeuButton type="button" data-testid="notify-preview" onClick={preview}>حساب العدد</NeuButton>
            <NeuButton type="button" variant="primary" data-testid="notify-send" disabled={busy} onClick={send}>{busy ? "جارٍ الإرسال…" : "إرسال"}</NeuButton>
          </div>
        </div>
      </NeuCard>
      <NotifyHistory refreshKey={sent} />
    </div>
  );
};
